import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchQuantityRules,
  type QuantityPriceRule,
  type QuantityRuleChannel,
  type QuantityRulesStatus,
} from "@/lib/quantityPriceRules";

const RETRY_DELAYS_MS = [1500, 4000, 10000];

/**
 * Carrega as regras de preço por quantidade do canal, com novas tentativas
 * automáticas. Em falha, mantém as últimas regras válidas (se houver) e
 * expõe status "error" para bloquear a confirmação.
 */
export function useQuantityRules(channel: QuantityRuleChannel) {
  const [rules, setRules] = useState<QuantityPriceRule[]>([]);
  const [status, setStatus] = useState<QuantityRulesStatus>("loading");
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setStatus("loading");
    for (let attempt = 0; ; attempt++) {
      try {
        const r = await fetchQuantityRules(channel);
        if (my !== seq.current) return;
        setRules(r);
        setStatus("ok");
        return;
      } catch (e) {
        if (my !== seq.current) return;
        if (attempt >= RETRY_DELAYS_MS.length) {
          console.warn("[quantityRules] falha ao carregar regras", e);
          setStatus("error");
          return;
        }
        await new Promise((res) => setTimeout(res, RETRY_DELAYS_MS[attempt]));
        if (my !== seq.current) return;
      }
    }
  }, [channel]);

  useEffect(() => {
    load();
    return () => { seq.current++; };
  }, [load]);

  return { rules, status, retry: load };
}
